import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import { VirtualAssistantButton } from '@/components/virtual-assistant/virtual-assistant-button';
import { NS } from '@/constants';
import { DESIGN_SYSTEM_SIZES } from '@/constants/styles';
import { URLS } from '@/constants/urls';
import { useAppSelector } from '@/redux';
import { selectLocale } from '@/redux/slices/root.slice';
import { CallToActionKind } from '@/types';
import styles from './virtual-assistant-buttons.module.scss';

export interface CustomWindow extends Window {
	SpeechRecognition?: any;
	webkitSpeechRecognition?: any;
}

export const VirtualAssistantButtons = ({ value, onChange, onClick }) => {
	const { t } = useTranslation();
	const locale = useAppSelector(selectLocale);
	const [listening, setListening] = useState(false);
	const recognitionRef = useRef<any | null>(null);
	const [microAvailable, setMicroAvailable] = useState(true);

	useEffect(() => {
		const SpeechRecognition = (window as CustomWindow).SpeechRecognition || (window as CustomWindow).webkitSpeechRecognition;

		if (!SpeechRecognition || !SpeechRecognition.available) {
			setMicroAvailable(false);
			return;
		}

		const recognition = new SpeechRecognition();
		let finalText = '';

		recognition.lang = locale || 'fr-FR';
		recognition.continuous = true;
		recognition.interimResults = true;

		recognition.onresult = (event) => {
			let currentText = '';
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) {
					finalText += result[0].transcript;
				} else {
					currentText += result[0].transcript;
				}
			}

			onChange(finalText + currentText);
		};

		recognition.onend = () => {
			setListening(false);
			finalText = '';
		};
		recognition.onerror = (e) => console.error('Erreur :', e.error);
		recognitionRef.current = recognition;

		return () => recognition.abort();
	}, []);

	const toggle = (e) => {
		e.stopPropagation();
		if (!recognitionRef.current) {
			return;
		}
		if (listening) {
			recognitionRef.current.stop();
		} else {
			recognitionRef.current.start();
			setListening(true);
		}
	};

	return listening ? (
		<CallToActionContainer
			onClick={toggle}
			kind={CallToActionKind.PRIMARY}
			buttonSize={DESIGN_SYSTEM_SIZES.SMALL}
			icon={{ url: URLS.SOUND_WAVE, alt: t('listen', NS.ALTER) }}
			className={styles.button}
		/>
	) : microAvailable && (value == null || value === '') ? (
		<CallToActionContainer
			onClick={toggle}
			kind={CallToActionKind.TERTIARY}
			icon={{ url: URLS.MICROPHONE, alt: t('micro', NS.ALTER) }}
			buttonSize={DESIGN_SYSTEM_SIZES.SMALL}
		/>
	) : (
		<VirtualAssistantButton
			size="sm"
			onClick={() => onClick(value)}
			icon={{ src: URLS.SEND_MESSAGE_WHITE, alt: t('assistant.ask'), width: 16, height: 16 }}
		/>
	);
};

export default VirtualAssistantButtons;
